'use client';

import ResourceSlider from '@/components/ResourceSlider';
import { useEffect, useMemo, useState } from 'react';
import { settings } from '../../../config/settings';
import { useTranslations } from 'next-intl';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Calculator,
  Check,
  Clock3,
  Cpu,
  Database,
  DollarSign,
  HardDrive,
  X,
} from 'lucide-react';

const factorColumns = [
  { key: 'free_tier', label: 'Free Tier', type: 'tier', showPrice: false },
  { key: 'budget_tier', label: 'Budget Tier', type: 'tier', showPrice: true },
  { key: 'premium_tier', label: 'Premium Tier', type: 'tier', showPrice: true },
  { key: 'extreme_tier', label: 'Extreme Tier', type: 'tier', showPrice: true },
  { key: 'quality_support_24_7', label: '24/7 Quality Support', type: 'boolean' },
  { key: 'custom_panel', label: 'Custom Panel', type: 'boolean' },
  { key: 'plugin_mod_installer', label: 'Plugin/Mod Installer', type: 'boolean' },
  { key: 'bedrock_support', label: 'Bedrock Support', type: 'boolean' },
];

const planKeys = ['free', 'budget', 'premium', 'extreme'];

const compairConfig = settings.compair_page || {
  title: '',
  description: '',
  hosts: [],
};

const hosts = Array.isArray(compairConfig.hosts) ? compairConfig.hosts : [];
const hostingConfig = (settings.hosting || {
  plans: {},
  pricing: {},
}) as any;

function toNumber(value, fallback = 0) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function parseResourceRange(value, fallback = 0) {
  if (value && typeof value === 'object') {
    const min = toNumber(value.min, toNumber(value.default, fallback));
    const max = Math.max(min, toNumber(value.max, min));
    const defaultValue = clamp(toNumber(value.default, min), min, max);
    return { min, max, defaultValue };
  }

  const fixed = toNumber(value, fallback);
  return {
    min: fixed,
    max: fixed,
    defaultValue: fixed,
  };
}

function buildPlanModel(planKey) {
  const plan = hostingConfig.plans?.[planKey] || {};
  const isFree = planKey === 'free';
  const configurable = Boolean(plan.configurable) && !isFree;

  const ram = parseResourceRange(plan.ram, isFree ? 2 : 4);
  const storage = parseResourceRange(plan.storage, isFree ? 5 : 25);
  const cpu = parseResourceRange(plan.cpu, isFree ? 4 : 4);

  return {
    key: planKey,
    name: String(plan.name || planKey).trim(),
    configurable,
    pricePerGbRam: toNumber(plan.price_per_gb_ram, 0),
    ram,
    storage,
    cpu,
  };
}

function formatCurrency(amount) {
  return `$${Number(amount || 0).toFixed(2)}`;
}

function isTierEnabled(tier) {
  if (!tier || typeof tier !== 'object') return false;
  return Boolean(tier.enabled);
}

function NoCell() {
  return (
    <div className="flex items-center justify-center py-3">
      <X className="h-5 w-5 text-red-500" />
    </div>
  );
}

function YesNoCell({ value }) {
  return (
    <div className="flex items-center justify-center py-3">
      {value ? <Check className="h-5 w-5 text-emerald-500" /> : <X className="h-5 w-5 text-red-500" />}
    </div>
  );
}

function TierCell({ tier, showPrice }) {
  const t = useTranslations('Pricing');
  if (!isTierEnabled(tier)) {
    return <NoCell />;
  }

  const details = [
    { key: 'uptime', label: t('uptime'), value: tier.uptime, Icon: Clock3 },
    { key: 'ram', label: t('ram'), value: tier.ram, Icon: Database },
    { key: 'cpu', label: t('cpu'), value: tier.cpu, Icon: Cpu },
    { key: 'storage', label: t('storage'), value: tier.storage, Icon: HardDrive },
  ];

  if (showPrice) {
    details.unshift({ key: 'price', label: t('price'), value: tier.price, Icon: DollarSign });
  }

  const visibleDetails = details.filter((item) => String(item.value || '').trim());

  if (visibleDetails.length === 0) {
    return (
      <div className="flex items-center justify-center py-3">
        <Check className="h-5 w-5 text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="space-y-1.5 py-2">
      {visibleDetails.map((item) => (
        <div key={item.key} className="flex items-center gap-1.5 text-xs leading-tight text-muted-foreground">
          <item.Icon className="h-3.5 w-3.5 shrink-0 text-foreground/80" />
          <span className="font-medium text-foreground/90">{item.label}:</span>
          <span>{item.value}</span>
        </div>
      ))}
    </div>
  );
}

export default function PricingPage() {
  const t = useTranslations('Pricing');
  const [selectedPlanKey, setSelectedPlanKey] = useState('budget');
  const selectedPlan = useMemo(() => buildPlanModel(selectedPlanKey), [selectedPlanKey]);

  const [ram, setRam] = useState(selectedPlan.ram.defaultValue);
  const [storage, setStorage] = useState(selectedPlan.storage.defaultValue);
  const [cpu, setCpu] = useState(selectedPlan.cpu.defaultValue);

  useEffect(() => {
    setRam(selectedPlan.ram.defaultValue);
    setStorage(selectedPlan.storage.defaultValue);
    setCpu(selectedPlan.cpu.defaultValue);
  }, [selectedPlan.key, selectedPlan.ram.defaultValue, selectedPlan.storage.defaultValue, selectedPlan.cpu.defaultValue]);

  const pricingModel = useMemo(() => {
    const freeStorageGb = toNumber(hostingConfig.pricing?.free_storage_gb, 25);
    const pricePer25GbStorage = toNumber(hostingConfig.pricing?.price_per_25gb_storage, 1);
    const freeCpuCores = toNumber(hostingConfig.pricing?.free_cpu_cores, 4);
    const pricePerExtraCore = toNumber(hostingConfig.pricing?.price_per_extra_core, 2.5);

    if (!selectedPlan.configurable || selectedPlan.key === 'free') {
      return {
        ramCost: 0,
        storageCost: 0,
        cpuCost: 0,
        total: 0,
      };
    }

    const ramCost = ram * selectedPlan.pricePerGbRam;
    const extraStorage = Math.max(0, storage - freeStorageGb);
    const storageCost = (extraStorage / 25) * pricePer25GbStorage;
    const extraCpu = Math.max(0, cpu - freeCpuCores);
    const cpuCost = extraCpu * pricePerExtraCore;
    const total = Math.round((ramCost + storageCost + cpuCost) * 100) / 100;

    return {
      ramCost,
      storageCost,
      cpuCost,
      total,
    };
  }, [selectedPlan, ram, storage, cpu]);

  return (
    <main className="mx-auto w-full max-w-[90rem] px-4 py-12 sm:px-6 lg:px-8">
      <div className="space-y-2">
        <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">{t('title')}</h1>
        <p className="max-w-3xl text-sm text-muted-foreground sm:text-base">
          {t('subtitle')}
        </p>
      </div>

      <Card className="mt-6 border-border/80">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Calculator className="h-5 w-5 text-primary" />
            {t('calculator')}
          </CardTitle>
          <CardDescription>
            {t('calculatorDesc')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex flex-wrap gap-2">
            {planKeys.map((planKey) => {
              const plan = buildPlanModel(planKey);
              const isSelected = selectedPlanKey === planKey;
              return (
                <Button
                  key={planKey}
                  type="button"
                  variant={isSelected ? 'default' : 'outline'}
                  onClick={() => setSelectedPlanKey(planKey)}
                >
                  {plan.name || planKey}
                </Button>
              );
            })}
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div className="space-y-5">
              <ResourceSlider label={t('ram')} value={ram} onChange={setRam} min={selectedPlan.ram.min} max={selectedPlan.ram.max} step={1} unit="GB" disabled={!selectedPlan.configurable} />

              <ResourceSlider label={t('storage')} value={storage} onChange={setStorage} min={selectedPlan.storage.min} max={selectedPlan.storage.max} step={25} unit="GB" disabled={!selectedPlan.configurable} />

              <ResourceSlider label={t('cpuCores')} value={cpu} onChange={setCpu} min={selectedPlan.cpu.min} max={selectedPlan.cpu.max} step={1} unit={t('cores')} disabled={!selectedPlan.configurable} />

              {!selectedPlan.configurable ? (
                <p className="rounded-lg border bg-muted/30 p-3 text-xs text-muted-foreground">
                  {t('fixedResources')}
                </p>
              ) : null}
            </div>

            <div className="rounded-xl border bg-muted/20 p-4">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium">{t('estimated')}</p>
                <Badge variant="outline">{selectedPlan.name}</Badge>
              </div>

              <p className="mt-2 text-3xl font-bold tracking-tight">
                {selectedPlan.key === 'free' ? t('free') : formatCurrency(pricingModel.total)}
              </p>

              <div className="mt-4 space-y-2 text-sm text-muted-foreground">
                <div className="flex items-center justify-between">
                  <span>{t('ramCost')}</span>
                  <span>{formatCurrency(pricingModel.ramCost)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span>{t('storageCost')}</span>
                  <span>{formatCurrency(pricingModel.storageCost)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span>{t('cpuCost')}</span>
                  <span>{formatCurrency(pricingModel.cpuCost)}</span>
                </div>
                <div className="h-px bg-border" />
                <div className="flex items-center justify-between text-foreground">
                  <span className="font-semibold">{t('total')}</span>
                  <span className="font-semibold">
                    {selectedPlan.key === 'free' ? t('free') : formatCurrency(pricingModel.total)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="mt-8 border-border/80">
        <CardHeader>
          <CardTitle className="text-lg">{t('compareTitle')}</CardTitle>
          <CardDescription>
            {t('compareDesc')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {hosts.length === 0 ? (
            <div className="rounded-lg border border-dashed p-6 text-sm text-muted-foreground">
              {t('noHosts')}
            </div>
          ) : (
            <div className="overflow-x-auto rounded-xl border">
              <Table className="min-w-[1160px]">
                <TableHeader>
                  <TableRow className="bg-muted/30">
                    <TableHead className="sticky left-0 z-20 min-w-[210px] bg-muted/60 backdrop-blur">
                      {t('hosts')}
                    </TableHead>
                    {factorColumns.map((factor) => (
                      <TableHead key={factor.key} className="min-w-[170px] text-xs uppercase tracking-wide text-muted-foreground">
                        {t(`factors.${factor.key}`)}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {hosts.map((host) => (
                    <TableRow key={host.name}>
                      <TableCell className="sticky left-0 z-10 min-w-[210px] bg-card/95 align-top backdrop-blur">
                        <div className="space-y-1">
                          <p className="font-semibold leading-tight">{host.name}</p>
                          {host.featured ? <Badge variant="secondary">{t('recommended')}</Badge> : null}
                        </div>
                      </TableCell>

                      {factorColumns.map((factor) => {
                        const value = host?.[factor.key];
                        return (
                          <TableCell key={`${host.name}-${factor.key}`} className="align-top">
                            {factor.type === 'boolean' ? (
                              <YesNoCell value={Boolean(value)} />
                            ) : (
                              <TierCell tier={value} showPrice={Boolean(factor.showPrice)} />
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <p className="mt-6 text-center text-sm text-muted-foreground">
        {t('suggest')}
      </p>
    </main>
  );
}
